// src/pages/DataPrivacyPage.jsx
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Shield,
  Lock,
  Cloud,
  Smartphone,
  CheckCircle,
  AlertCircle,
  Download,
  Trash2,
  Eye,
  EyeOff,
  HardDrive,
  Settings,
  ChevronLeft,
  BarChart3,
  ExternalLink
} from 'lucide-react';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import {
  hasAnalyticsPreference,
  setAnalyticsPreference,
  ANALYTICS_PREFERENCE_EVENT
} from '../services/analytics';
import { DDD_PRIVACY_URL, DDD_TERMS_URL, DDD_COOKIES_URL } from '../config/legalLinks';
import { clearModuleContext } from '../services/coachModuleContext';

// Deleting the DDD account in the hub deletes everything the hub holds for
// the person, this app's cloud backup included.
const HUB_ACCOUNT_DELETE_URL = `${import.meta.env.VITE_AUTH_HUB_URL || 'https://auth.divedeepdialogues.com'}/account/delete`;

const POLICY_LINKS = [
  { label: 'Privacy Policy', href: DDD_PRIVACY_URL },
  { label: 'Terms of Use', href: DDD_TERMS_URL },
  { label: 'Cookie Policy', href: DDD_COOKIES_URL }
];

// Everything this app keeps on this device lives in localStorage for this
// site, so read the real figures rather than showing placeholders.
const readLocalData = () => {
  const data = {};
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      data[key] = localStorage.getItem(key);
    }
  } catch (error) {
    console.error('Error reading local data:', error);
  }
  return data;
};

const computeStorageStats = () => {
  const data = readLocalData();
  const bytes = Object.entries(data).reduce(
    (sum, [key, value]) => sum + (key.length + (value ? value.length : 0)) * 2,
    0
  );
  const totalSize = bytes < 1024 * 1024
    ? `${Math.max(1, Math.round(bytes / 1024))} KB`
    : `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return { totalSize, itemCount: Object.keys(data).length };
};

const DataPrivacyPage = () => {
  const { cloudSyncEnabled, enableCloudSync, disableCloudSync, deleteCloudBackup } = useApp();
  const preferences = { cloudSync: cloudSyncEnabled };

  // The header's Settings button opens /privacy?tab=settings.
  const [searchParams] = useSearchParams();
  const requestedTab = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState(
    ['overview', 'settings', 'management'].includes(requestedTab) ? requestedTab : 'overview'
  );
  const [storageStats, setStorageStats] = useState(null);
  const [operationStatus, setOperationStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  // While backup is being switched on and the account is answering.
  const [backupBusy, setBackupBusy] = useState(false);
  const [analyticsOn, setAnalyticsOn] = useState(hasAnalyticsPreference);
  const { user } = useAuth();
  const isSignedIn = !!user;

  const loadStorageStats = () => setStorageStats(computeStorageStats());

  useEffect(() => {
    setStorageStats(computeStorageStats());
  }, []);

  // A decision made on the first-visit notice while this page is open
  useEffect(() => {
    const sync = (event) => setAnalyticsOn(Boolean(event.detail));
    window.addEventListener(ANALYTICS_PREFERENCE_EVENT, sync);
    return () => window.removeEventListener(ANALYTICS_PREFERENCE_EVENT, sync);
  }, []);

  const handleAnalyticsToggle = () => {
    const next = !analyticsOn;
    setAnalyticsPreference(next);
    setAnalyticsOn(next);
    setOperationStatus({
      type: 'success',
      message: next ? 'Usage analytics switched on. Thank you!' : 'Usage analytics switched off.'
    });
    setTimeout(() => setOperationStatus(null), 3000);
  };

  const showStatus = (type, message, ms = 4000) => {
    setOperationStatus({ type, message });
    setTimeout(() => setOperationStatus(null), ms);
  };

  const handlePreferenceChange = async (key, value) => {
    if (key === 'cloudSync' && !isSignedIn && value === true) {
      alert('Please sign in to switch cloud backup on.');
      return;
    }

    if (key !== 'cloudSync') return;
    if (value) {
      // The switch flips straight away (enableCloudSync sets it before the
      // account answers); the popup follows once the account has replied.
      setBackupBusy(true);
      let ok = false;
      try {
        ok = await enableCloudSync();
      } finally {
        setBackupBusy(false);
      }
      if (ok) {
        showStatus('success', 'Cloud backup is on. Your work is being saved to your Dive Deep Dialogues account.');
      } else {
        showStatus('error', "Cloud backup couldn't reach your account just now. It will keep trying while it's on.", 6000);
      }
    } else {
      await disableCloudSync();
      showStatus('success', 'Cloud backup is off. New work stays on this device. What is already in your account stays there until you delete it.', 6000);
    }
  };

  const handleDeleteCloudBackup = async () => {
    if (!isSignedIn) return;
    const confirmed = window.confirm(
      'Delete everything Relationship Compass has backed up to your Dive Deep Dialogues account (assessment results, progress, tool entries, points and achievements)? Data on this device stays, and cloud backup will be switched off. This cannot be undone.'
    );
    if (!confirmed) return;

    try {
      setLoading(true);
      const ok = await deleteCloudBackup();
      if (ok) {
        showStatus('success', 'Your cloud backup is deleted, and cloud backup is off. Switch it on in Settings to back up again.', 6000);
      } else {
        showStatus('error', "Your cloud backup couldn't be deleted. Please try again, or email help@divedeepdialogues.com.", 6000);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleExportData = async () => {
    try {
      setLoading(true);
      const data = {
        exportedAt: new Date().toISOString(),
        source: 'Relationship Compass (data stored on this device)',
        localStorage: readLocalData()
      };

      const blob = new Blob([JSON.stringify(data, null, 2)], {
        type: 'application/json'
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `relationship-compass-backup-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      setOperationStatus({
        type: 'success',
        message: 'Data exported successfully!'
      });

      setTimeout(() => setOperationStatus(null), 3000);
    } catch {
      setOperationStatus({
        type: 'error',
        message: 'Export failed. Please try again.'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClearAllData = async () => {
    const confirmed = window.confirm(
      'Delete all Relationship Compass data stored on this device? This cannot be undone.'
    );

    if (!confirmed) return;

    try {
      setLoading(true);
      localStorage.clear();
      clearModuleContext();

      setOperationStatus({
        type: 'success',
        message: 'Data on this device cleared. Restarting the app…'
      });

      loadStorageStats();
      // Reload so nothing from before the clear (including an analytics
      // session the user had allowed) carries on in memory. The analytics
      // question is asked again on the fresh start.
      setTimeout(() => window.location.reload(), 1500);
    } catch {
      setOperationStatus({
        type: 'error',
        message: 'Failed to clear data.'
      });
    } finally {
      setLoading(false);
    }
  };

  const OverviewTab = () => (
    <div className="space-y-6">
      <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl border border-slate-700/50 p-6">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <HardDrive className="w-5 h-5 text-rose-400" />
          Your Data Storage
        </h3>

        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-slate-300">Storage Location:</span>
              <div className="flex items-center gap-2">
                <Smartphone className="w-4 h-4 text-green-400" />
                <span className="text-green-400">{preferences.cloudSync ? 'This device + your DDD account' : 'This device'}</span>
              </div>
            </div>

            <div className="flex justify-between items-center">
              <span className="text-slate-300">In transit:</span>
              <div className="flex items-center gap-2">
                <Lock className="w-4 h-4 text-green-400" />
                <span className="text-green-400">Encrypted (HTTPS)</span>
              </div>
            </div>

            <div className="flex justify-between items-center">
              <span className="text-slate-300">Data Size:</span>
              <span className="text-white">{storageStats?.totalSize || 'Calculating...'}</span>
            </div>

            <div className="flex justify-between items-center">
              <span className="text-slate-300">Items Stored:</span>
              <span className="text-white">{storageStats?.itemCount || 0}</span>
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-slate-300">Cloud Backup:</span>
              <div className="flex items-center gap-2">
                {preferences.cloudSync ? (
                  <>
                    <Cloud className="w-4 h-4 text-blue-400" />
                    <span className="text-blue-400">Enabled</span>
                  </>
                ) : (
                  <>
                    <EyeOff className="w-4 h-4 text-slate-400" />
                    <span className="text-slate-400">Local Only</span>
                  </>
                )}
              </div>
            </div>

            <div className="flex justify-between items-center">
              <span className="text-slate-300">Partner sharing:</span>
              <span className="text-white">Only if you start or join a session</span>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-gradient-to-r from-green-900/20 to-blue-900/20 border border-green-500/30 rounded-2xl p-6">
        <div className="flex items-start gap-4">
          <Shield className="w-8 h-8 text-green-400 mt-1" />
          <div>
            <h3 className="text-lg font-semibold text-white mb-2">Where Your Data Goes</h3>
            <div className="space-y-2 text-slate-300">
              <p className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-green-400 mt-1 shrink-0" />
                Your answers, progress and tool entries are saved on this device. If you&apos;re signed in with cloud backup on, they&apos;re also saved to your Dive Deep Dialogues account (held in Google Firebase), so you can use them on other devices. Deleting your DDD account deletes that copy too.
              </p>
              <p className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-green-400 mt-1 shrink-0" />
                If you start or join a partner session, the first names, answers and scores for that session are stored in Relationship Compass&apos;s own Firebase project, not in anyone&apos;s account. Anyone with the session code can open it until it closes, 7 days after its last activity.
              </p>
              <p className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-green-400 mt-1 shrink-0" />
                If you use the AI coach, your messages are sent to Anthropic&apos;s Claude to write each reply (see the coach&apos;s privacy page).
              </p>
              <p className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-green-400 mt-1 shrink-0" />
                Usage analytics (Google Analytics) is off unless you say yes, either on the first-visit notice or under Settings. It never receives your answers, notes, messages or names.
              </p>
              <p className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-green-400 mt-1 shrink-0" />
                Data on this device isn&apos;t encrypted by the app, so anyone who can use this browser can see it. You can export or delete it any time on the Data Management tab.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const SettingsTab = () => (
    <div className="space-y-6">
      <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl border border-slate-700/50 p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Cloud className="w-5 h-5 text-blue-400" />
            <h3 className="text-lg font-semibold text-white">Cloud Backup</h3>
            {!isSignedIn && (
              <div className="flex items-center gap-1 px-2 py-1 bg-blue-500/20 text-blue-300 rounded text-xs">
                <Lock className="w-3 h-3" />
                Sign In Required
              </div>
            )}
          </div>
        </div>

        <div className="space-y-4">
          <div className="flex items-center justify-between gap-4 p-4 bg-white/5 rounded-lg">
            <div>
              <h4 className="text-white font-medium">Enable Cloud Backup</h4>
              <p className="text-slate-400 text-sm">
                {isSignedIn ? 'Save your work to your Dive Deep Dialogues account so you can use it on other devices. Turn it on for each device you use.' : 'Sign in to back up your work to your Dive Deep Dialogues account'}
              </p>
            </div>
            <button
              onClick={() => handlePreferenceChange('cloudSync', !preferences.cloudSync)}
              role="switch"
              aria-checked={!!preferences.cloudSync}
              aria-label="Cloud backup"
              aria-busy={backupBusy}
              disabled={!isSignedIn || backupBusy}
              className={`w-12 h-6 shrink-0 rounded-full transition-colors relative ${
                preferences.cloudSync ? 'bg-blue-500' : 'bg-slate-600'
              } ${!isSignedIn ? 'opacity-50 cursor-not-allowed' : ''} ${backupBusy ? 'cursor-wait' : ''}`}
            >
              <motion.div
                className="w-5 h-5 rounded-full bg-white absolute top-0.5"
                initial={false}
                animate={{ left: preferences.cloudSync ? '1.75rem' : '0.25rem' }}
              />
            </button>
          </div>
        </div>
      </div>

      <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl border border-slate-700/50 p-6">
        <div className="flex items-center justify-between gap-4 p-4 bg-white/5 rounded-lg">
          <div>
            <h3 className="text-white font-medium flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-green-400" aria-hidden="true" />
              Usage analytics (opt-in)
            </h3>
            <p className="text-slate-300 text-sm mt-1">
              Off unless you switch it on. When it&apos;s on, Google Analytics records which pages are visited, along with the standard browser, device and approximate-location details it collects. It never receives your answers, notes, messages or names, and partner session codes are removed from page addresses first.
            </p>
          </div>
          <button
            onClick={handleAnalyticsToggle}
            role="switch"
            aria-checked={analyticsOn}
            aria-label="Usage analytics"
            className={`w-12 h-6 shrink-0 rounded-full transition-colors relative ${
              analyticsOn ? 'bg-green-600' : 'bg-slate-600'
            }`}
          >
            <motion.div
              className="w-5 h-5 rounded-full bg-white absolute top-0.5"
              initial={false}
              animate={{ left: analyticsOn ? '1.75rem' : '0.25rem' }}
            />
          </button>
        </div>
      </div>
    </div>
  );

  const ManagementTab = () => (
    <div className="space-y-6">
      <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl border border-slate-700/50 p-6">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Download className="w-5 h-5 text-blue-400" />
          Export Your Data
        </h3>

        <p className="text-slate-300 mb-4">
          Download everything Relationship Compass has saved on this device, in JSON format.
          This includes your assessments, progress, tool entries and settings.
        </p>

        <button
          onClick={handleExportData}
          disabled={loading}
          className="inline-flex items-center gap-2 px-6 py-3 bg-rose-600 hover:bg-rose-700 text-white rounded-full transition-colors disabled:opacity-50"
        >
          <Download className="w-4 h-4" />
          {loading ? 'Exporting...' : 'Export All Data'}
        </button>
      </div>

      <div className="bg-red-900/20 border border-red-500/30 rounded-2xl p-6">
        <h3 className="text-lg font-semibold text-red-300 mb-4 flex items-center gap-2">
          <Cloud className="w-5 h-5" />
          Delete Your Cloud Backup
        </h3>

        <p className="text-slate-300 mb-4">
          {isSignedIn ? (
            <>
              Permanently delete everything Relationship Compass has backed up to your Dive Deep
              Dialogues account: assessment results, progress, tool entries, points and achievements.
              <strong className="text-red-300"> This cannot be undone.</strong> Data on this device
              stays (Clear All Data below removes that), and cloud backup is switched off so nothing is
              saved straight back.
            </>
          ) : (
            'Sign in to delete what is backed up to your account. Without an account, nothing is backed up.'
          )}
        </p>

        <button
          onClick={handleDeleteCloudBackup}
          disabled={loading || !isSignedIn}
          className="inline-flex items-center gap-2 px-6 py-3 bg-red-600 hover:bg-red-700 text-white rounded-full transition-colors disabled:opacity-50"
        >
          <Cloud className="w-4 h-4" />
          {loading ? 'Deleting...' : 'Delete Cloud Backup'}
        </button>

        <p className="text-slate-400 text-sm mt-4">
          To delete your whole Dive Deep Dialogues account, with everything it holds for every DDD
          app, go to{' '}
          <a
            href={HUB_ACCOUNT_DELETE_URL}
            className="text-rose-300 hover:text-white underline inline-flex items-center gap-1"
          >
            Delete my DDD account
            <ExternalLink className="w-3 h-3" aria-hidden="true" />
          </a>
          .
        </p>
      </div>

      <div className="bg-red-900/20 border border-red-500/30 rounded-2xl p-6">
        <h3 className="text-lg font-semibold text-red-300 mb-4 flex items-center gap-2">
          <Trash2 className="w-5 h-5" />
          Clear All Data
        </h3>

        <p className="text-slate-300 mb-4">
          Permanently delete everything Relationship Compass has saved on this device.
          <strong className="text-red-300"> This cannot be undone.</strong> Your cloud backup has its
          own button above. Partner sessions close automatically 7 days after their last activity.
        </p>

        <button
          onClick={handleClearAllData}
          disabled={loading}
          className="inline-flex items-center gap-2 px-6 py-3 bg-red-600 hover:bg-red-700 text-white rounded-full transition-colors disabled:opacity-50"
        >
          <Trash2 className="w-4 h-4" />
          {loading ? 'Clearing...' : 'Clear All Data'}
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 pt-32">
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Back Button */}
          <Link
            to="/"
            className="flex items-center gap-2 text-rose-300 hover:text-white mb-8 transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
            Back to Home
          </Link>

          <div className="text-center mb-8">
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Data & Privacy
            </h1>
            <p className="text-lg text-slate-300">
              What the app stores, where it goes, and how to export or delete it.
            </p>
            <p className="text-sm text-slate-400 mt-3">
              Our privacy policy, terms and cookie policy are shared across all Dive Deep Dialogues apps:
            </p>
            <ul className="mt-2 flex flex-wrap justify-center gap-x-5 gap-y-1 text-sm">
              {POLICY_LINKS.map((link) => (
                <li key={link.href}>
                  <a
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 py-1 text-rose-300 hover:text-white underline"
                  >
                    {link.label}
                    <ExternalLink className="w-3 h-3" aria-hidden="true" />
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div className="flex justify-center mb-8">
            <div className="bg-slate-800/50 rounded-lg p-1 flex flex-wrap justify-center gap-1">
              {[
                { id: 'overview', label: 'Overview', icon: Eye },
                { id: 'settings', label: 'Settings', icon: Settings },
                { id: 'management', label: 'Data Management', icon: HardDrive }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`min-h-[44px] px-3 sm:px-4 py-2 rounded-md font-medium transition-all flex items-center gap-2 ${
                    activeTab === tab.id
                      ? 'bg-rose-600 text-white'
                      : 'text-slate-300 hover:text-white hover:bg-slate-700'
                  }`}
                >
                  <tab.icon className="w-4 h-4" />
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          <AnimatePresence mode="wait">
            {activeTab === 'overview' && (
              <motion.div
                key="overview"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
              >
                {/* Called, not rendered as <OverviewTab />: these are defined inside the
                    page, so as components they were rebuilt on every state change and
                    replayed their switch animations (e.g. when a status popup came or went). */}
                {OverviewTab()}
              </motion.div>
            )}

            {activeTab === 'settings' && (
              <motion.div
                key="settings"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
              >
                {SettingsTab()}
              </motion.div>
            )}

            {activeTab === 'management' && (
              <motion.div
                key="management"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
              >
                {ManagementTab()}
              </motion.div>
            )}
          </AnimatePresence>

          {operationStatus && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className={`fixed bottom-4 right-4 p-4 rounded-lg shadow-lg max-w-sm ${
                operationStatus.type === 'success'
                  ? 'bg-green-900/90 text-green-300 border border-green-500/30'
                  : 'bg-red-900/90 text-red-300 border border-red-500/30'
              }`}
            >
              <div className="flex items-center gap-2">
                {operationStatus.type === 'success' ? (
                  <CheckCircle className="w-5 h-5" />
                ) : (
                  <AlertCircle className="w-5 h-5" />
                )}
                <span>{operationStatus.message}</span>
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DataPrivacyPage;
